import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Music, Megaphone, UtensilsCrossed, Star, Gamepad2, Zap, Loader2, Power, AlertTriangle } from 'lucide-react';
import { Switch } from './ui/switch';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * LIVE CONTROLS — replaces the old home-screen remote MiniPlayer.
 *
 * Reason: the MiniPlayer was a *remote control* fed by the venue's DJ Console
 * heartbeat. The new global PlayerBar at the bottom is the *actual* same-tab
 * player. Having both caused inconsistent state (pause on Home did nothing
 * visible to the host). User requested we drop the home-screen player and put
 * the request feature toggles here so the host can flip guest experiences
 * on/off without leaving the dashboard.
 *
 * - When NO live session exists → show a one-tap GO LIVE CTA.
 * - When a session is live → show 6 toggles (Requests, Auto-Accept, Shoutouts,
 *   Reviews, Light Show, Games) wired to the same PUT
 *   /api/requests/sessions/{id}/settings endpoint Settings page uses.
 */
const TOGGLES = [
  { key: 'requests_enabled',   label: 'Song Requests',    desc: 'Guests can queue requests',           Icon: Music,          color: '#FF007F', defaultOn: true,  aiHidden: true },
  { key: 'auto_accept',        label: 'Auto-Accept',      desc: 'Skip the approval queue',             Icon: Zap,            color: '#FFD700', defaultOn: false, aiHidden: true },
  { key: 'shoutouts_enabled',  label: 'Shoutouts',        desc: 'Guests can send live shoutouts',      Icon: Megaphone,      color: '#A855F7', defaultOn: true },
  { key: 'reviews_enabled',    label: 'Reviews',          desc: 'Prompt for Google reviews',           Icon: Star,           color: '#FFD700', defaultOn: true },
  { key: 'games_enabled',      label: 'Bar Games',        desc: 'Coupons via games & quizzes',         Icon: Gamepad2,       color: '#39FF14', defaultOn: true },
];

const LiveControlsCard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAI = user?.client_type === 'ai';
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [savingKey, setSavingKey] = useState(null);

  const fetchSession = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/requests/sessions/active`);
      setSession(res.data || null);
    } catch (err) {
      console.warn('[LiveControls] fetchSession failed:', err);
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSession(); }, [fetchSession]);

  const goLive = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/requests/sessions`);
      setSession(res.data);
      toast.success('Session live!');
    } catch (err) {
      console.warn('[LiveControls] create failed:', err);
      toast.error('Failed to start session');
    } finally {
      setCreating(false);
    }
  };

  const closeSession = async () => {
    if (!session) return;
    if (!window.confirm("End this session? Guests won't be able to submit requests.")) return;
    try {
      await axios.delete(`${API}/requests/sessions/${session.id}`);
      setSession(null);
      toast.success('Session ended');
    } catch (err) {
      console.warn('[LiveControls] end failed:', err);
      toast.error('Failed to end session');
    }
  };

  const updateFlag = async (key, value) => {
    if (!session) return;
    setSavingKey(key);
    try {
      const res = await axios.put(`${API}/requests/sessions/${session.id}/settings`, { [key]: value });
      setSession(res.data);
    } catch (err) {
      console.warn(`[LiveControls] updateFlag(${key}) failed:`, err);
      toast.error('Failed to update');
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#0D0D0D] border border-white/8 rounded-2xl p-5 flex items-center justify-center min-h-[200px]" data-testid="live-controls-loading">
        <Loader2 className="w-5 h-5 text-gray-500 animate-spin" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="bg-[#0D0D0D] border border-white/8 rounded-2xl p-5" data-testid="live-controls-offline">
        <div className="flex items-center gap-2 mb-3">
          <span className="w-2 h-2 rounded-full bg-gray-600" />
          <h3 className="text-[11px] uppercase tracking-[0.3em] text-gray-500 font-['JetBrains_Mono'] font-bold">
            Offline
          </h3>
        </div>
        <p className="text-white font-bold text-lg font-['Outfit'] mb-1">Ready to go live?</p>
        <p className="text-gray-500 text-[13px] leading-relaxed mb-5">
          Start a session to accept guest song requests, shoutouts, light show sync, and game coupons.
        </p>
        <Button
          onClick={goLive}
          disabled={creating}
          className="w-full bg-[#FF007F] hover:bg-[#CC0066] text-white rounded-full h-11 font-bold tracking-wider"
          data-testid="live-controls-go-live"
        >
          {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Power className="w-4 h-4 mr-2" />}
          GO LIVE
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-[#0D0D0D] border border-white/8 rounded-2xl p-5" data-testid="live-controls-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#39FF14] animate-pulse" />
          <h3 className="text-[11px] uppercase tracking-[0.3em] text-[#39FF14] font-['JetBrains_Mono'] font-bold">
            Live
          </h3>
          <span className="text-[10px] font-mono text-gray-600 ml-1">· {session.code}</span>
        </div>
        <button
          onClick={closeSession}
          className="text-[10px] uppercase tracking-[0.25em] text-gray-500 hover:text-[#FF3B3B] font-bold transition-colors"
          data-testid="live-controls-end"
          title="End the session"
        >
          End
        </button>
      </div>

      <p className="text-white font-bold text-base font-['Outfit'] mb-1">Guest experiences</p>
      <p className="text-gray-500 text-[12px] leading-relaxed mb-4">
        Flip each one on or off live — guests see the change instantly on their phones.
      </p>

      <div className="space-y-2">
        {TOGGLES.filter(({ aiHidden }) => !(isAI && aiHidden)).map(({ key, label, desc, Icon, color, defaultOn }) => {
          const on = session[key] !== undefined ? !!session[key] : defaultOn;
          const busy = savingKey === key;
          return (
            <div
              key={key}
              className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-white/[0.02] transition-colors"
              data-testid={`live-toggle-row-${key}`}
            >
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${color}1F`, color }}
              >
                <Icon className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-white text-[13px] font-semibold leading-tight">{label}</p>
                <p className="text-gray-500 text-[10px] leading-tight mt-0.5">{desc}</p>
              </div>
              <Switch
                checked={on}
                onCheckedChange={(v) => updateFlag(key, v)}
                disabled={busy}
                data-testid={`live-toggle-${key}`}
              />
            </div>
          );
        })}
      </div>

      {!isAI && session.requests_enabled === false && (
        <div className="mt-4 flex items-start gap-2 p-3 rounded-xl bg-[#FF8C00]/10 border border-[#FF8C00]/30">
          <AlertTriangle className="w-3.5 h-3.5 text-[#FF8C00] flex-shrink-0 mt-0.5" />
          <p className="text-[11px] text-[#FF8C00] leading-relaxed">
            Song requests are paused — guests scanning the QR will see this option greyed out.
          </p>
        </div>
      )}

      {/* Vibe Democracy toggle — Feb 2026. Switches guests from search UI
          to a vote-on-playlist ballot. Requires the DJ to pick which
          playlist becomes the ballot pool. AI venues don't use ballots
          (their scheduler pulls from the in-house library), so hide it. */}
      {!isAI && (
        <VotingModePanel
          session={session}
          onSessionUpdate={(next) => setSession(next)}
        />
      )}

      {!isAI && (
        <Button
          onClick={() => navigate('/')}
          variant="ghost"
          className="w-full mt-4 border border-white/10 hover:bg-white/5 text-gray-300 hover:text-white rounded-xl text-xs font-bold tracking-wider"
          data-testid="live-controls-open-home"
        >
          OPEN PLAYBACK HOME →
        </Button>
      )}
    </div>
  );
};

// ---------- Vibe Democracy sub-panel ----------
const VotingModePanel = ({ session, onSessionUpdate }) => {
  const [playlists, setPlaylists] = useState([]);
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState(session?.active_playlist_id || '');

  // Fetch playlists once — feeds the ballot-source dropdown.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/playlists`).then((r) => {
      if (cancelled) return;
      const list = Array.isArray(r.data) ? r.data : (r.data?.playlists || []);
      setPlaylists(list.filter((p) => (p.tracks || []).length > 0));
    }).catch(() => { /* noop */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setSelectedId(session?.active_playlist_id || '');
  }, [session?.active_playlist_id]);

  const toggle = async (enable) => {
    if (enable && !selectedId) {
      toast.error('Pick a playlist first — the ballot pulls from it.');
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/requests/sessions/${session.code}/voting-mode`,
        enable ? { enabled: true, playlist_id: selectedId } : { enabled: false },
      );
      onSessionUpdate({
        ...session,
        voting_mode: r.data.voting_mode,
        active_playlist_id: r.data.active_playlist_id,
      });
      toast.success(enable ? 'Voting mode ON — guests are voting!' : 'Voting mode off');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not toggle voting mode');
    } finally {
      setBusy(false);
    }
  };

  const on = !!session?.voting_mode;
  return (
    <div
      className="mt-4 p-3 rounded-xl bg-[#FF007F]/5 border border-[#FF007F]/20"
      data-testid="voting-mode-panel"
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-[#FF007F]/15 flex items-center justify-center flex-shrink-0">
          <Music className="w-4 h-4 text-[#FF007F]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-white text-[13px] font-semibold leading-tight">Vibe Democracy</p>
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#FF007F]/20 text-[#FF007F] uppercase tracking-wider font-bold">
              Beta
            </span>
          </div>
          <p className="text-gray-500 text-[10px] leading-tight mt-0.5">
            Guests vote for what plays next from a playlist
          </p>
        </div>
        <Switch
          checked={on}
          onCheckedChange={toggle}
          disabled={busy || (!on && !selectedId)}
          data-testid="voting-mode-toggle"
        />
      </div>
      {!on && (
        <div className="mt-3 pl-11">
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            disabled={busy}
            className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-xs focus:outline-none focus:border-[#FF007F]/50"
            data-testid="voting-mode-playlist-select"
          >
            <option value="">Choose playlist for the ballot…</option>
            {playlists.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({(p.tracks || []).length})
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

export default LiveControlsCard;
